import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { useState } from "react";
import router, { useRouter } from "next/router";
import capitalize from "lodash/capitalize";

import { SalesLayout } from "components/SalesLayout";
import { ListingTables } from "components/ListingTables";
import { ShareListingModal } from "components/ShareListingModal";
import { DeleteListingModal } from "components/DeleteListingModal";
import { ArchiveListingModal } from "components/ArchiveListingModal";
import { Button } from "common/ui/Button";
import { Stack } from "common/ui/Stack";
import { Toast } from "common/ui/Toast";
import { type ToastVariants } from "common/ui/Toast";
import { ListingsIndexPageHeader } from "features/listings/components/ListingsIndexPageHeader/ListingsIndexPageHeader";
import { PublishConfirmationToast } from "features/listings/components/PublishConfirmationToast/PublishConfirmationToast";
import { NoListingsView } from "features/listings/components/NoListingsView/NoListingsView";

import {
  ListingStates,
  useLoggedInUserListings,
} from "api/getLoggedInUserListings";
import { ListingState } from "common/middleware/api.services";

export default function ListingsPage() {
  const { query } = useRouter();
  const { t } = useTranslation("admin");
  const { listings, isLoading, mutateLoggedInUserListings } = useLoggedInUserListings({
    filterByListingStates: Object.values(ListingStates).flat(),
  });

  const publishedListings = listings?.entities.filter((l) => (
    ListingStates.published.includes(ListingState[capitalize(l.state.toString())])
  ));
  const draftListings = listings?.entities.filter((l) => (
    ListingStates.draft.includes(ListingState[capitalize(l.state.toString())])
  ));
  const archivedListings = listings?.entities.filter((l) => (
    ListingStates.archived.includes(ListingState[capitalize(l.state.toString())])
  ));

  const getCurrListings = () => {
    switch(query.view) {
      case 'published':
        return publishedListings;
      case 'draft':
        return draftListings;
      case 'archived':
        return archivedListings;
      default:
        return listings?.entities
    }
  };

  const [idOfListingToArchive, setIdOfListingToArchive] = useState<number>();
  const [idOfListingToDelete, setIdOfListingToDelete] = useState<number>();
  const [listingLink, setListingLink] = useState("");
  const [toast, setToast] = useState<{
    msg: string,
    variant: ToastVariants,
  }>({
    msg: "",
    variant: undefined,
  });

  const handleDeleteListingSuccess = () => {
    const property = getCurrListings()?.find(l =>
      (l.parentListingId ?  l.parentListingId : l.id) === idOfListingToDelete
    );

    setToast({
      msg: t("listings.deleteSuccess", {
        address: property.address.unitNumber
          ? `${property.address.unitNumber} - ${property.address.street}`
          : property.address.street
      }),
      variant: "critical",
    });

    mutateLoggedInUserListings();

    setIdOfListingToDelete(undefined);
  };

  const handleArchiveSuccess = () => {
    const property = getCurrListings()?.find(l => l.id === idOfListingToArchive);

    setToast({
      msg: t("listings.archiveSuccess", {
        address: property.address.unitNumber
          ? `${property.address.unitNumber} - ${property.address.street}`
          : property.address.street
      }),
      variant: "success",
    });

    mutateLoggedInUserListings();

    setIdOfListingToArchive(undefined);
  }

  return (
    <>
      <SalesLayout
        header={<ListingsIndexPageHeader />}
      >
        {listings?.count === 0 ? (
          <NoListingsView />
        ) : (
          <>
            <Stack justifyContent="flex-end">
              <Button
                variant="primary"
                disabled={isLoading}
                onClick={() => router.push("/listings/property")}
              >
                {t("listings.createListing")}
              </Button>
            </Stack>
            <ListingTables
              listings={getCurrListings()}
              numOfDraftListings={draftListings?.length}
              numOfPublishedListings={publishedListings?.length}
              setListingLink={setListingLink}
              setIdOfListingToDelete={setIdOfListingToDelete}
              setIdOfListingToArchive={setIdOfListingToArchive}
              isLoading={isLoading}
            />
          </>
        )}
      </SalesLayout>
      {query.published && query.propertyId && (
        <PublishConfirmationToast />
      )}
      {idOfListingToDelete && (
        <DeleteListingModal
          close={() => setIdOfListingToDelete(undefined)}
          propertyId={idOfListingToDelete}
          closeOnSuccess={handleDeleteListingSuccess}
        />
      )}
      {idOfListingToArchive && (
        <ArchiveListingModal
          close={() => setIdOfListingToArchive(undefined)}
          propertyId={idOfListingToArchive}
          closeOnSuccess={handleArchiveSuccess}
        />
      )}
      {listingLink && (
        <ShareListingModal
          close={() => setListingLink("")}
          publicUrl={listingLink}
          setToast={setToast}
        />
      )}
      {toast.msg && (
        <Toast
          timeout={3000}
          variant={toast.variant}
          close={() => setToast({ msg: "", variant: undefined })}
          alertIcon
        >
          {toast.msg}
        </Toast>
      )}
    </>
  )
}

export const getStaticProps = async ({ locale }) => {
  return {
    props: {
      ...await serverSideTranslations(locale, [
        "common",
        "validation",
        "admin",
      ])
    }
  }
};
